import * as React from 'react';
import { toast } from 'sonner';
import { usePostHog } from 'posthog-js/react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  Upload,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  ArrowLeft,
  FileText,
  Link2,
  X,
  ArrowRight,
  Download,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { ConnectorIcon } from './connectorIcon';
import { Checkbox } from '@/components/ui/checkbox';
import { SourceIntentReviewPanel } from './SourceIntentReviewPanel';
import { ImportWizardProgress, type ImportWizardStep } from './ImportWizardProgress';
import { SourceImportReconciliationPanel } from './SourceImportReconciliationPanel';
import {
  sha256FileHash,
  useCreateSopFileJob,
} from '../../../hooks/useCreateSopFileJob';
import {
  useSourceLineageSuggestionsMutation,
  type SourceLineageSuggestion,
} from '../../../hooks/useSourceLineageSuggestions';
import { useAuthenticatedFetch } from '../../../hooks/useAuth';
import { useProcessFolderConfig } from '../../../domains/process/folders/useProcessFolderConfig';
import { useSetProcessFolderConfig } from '../../../domains/process/folders/useSetProcessFolderConfig';
import { resolveProcessFolderConfig } from '../../../domains/process/folders/processFolderConfig';
import {
  SpreadsheetIntentResolver,
  type SpreadsheetMappingReviewSelection,
  type SpreadsheetIntentResolution,
} from '../../../domains/knowledge-ingestion/services/SpreadsheetIntentResolver';
import type { SourceMappingProfile } from '../../../domains/knowledge-ingestion/models';
import type {
  ConnectorManifest,
  FolderKind,
  ImportTarget,
} from '../connectorRegistry';
import {
  applyTabularImport,
  fetchAllAdapterRecords,
  fetchAssistedHeaderRoles,
  grantOwnerImportFullAccess,
  headersFromRecords,
  preferredProfileId,
  previewTabularImport,
  tabularImportLimitError,
  TabularImportApplyError,
  type TabularImportBatch,
  type TabularImportPreviewResult,
} from '../services/tabularImport';
import {
  canConfirmIntentResolution,
  canConfirmMappingSelections,
  findSelectedIntentCandidate,
  initialMappingSelections,
  initialSelectedIntentForResolution,
} from '../services/intentReviewGate';
import { importableProfileFieldAliases, importableProfileFieldEntityRefs } from '../utils/importableProfileFieldAliases';
import { refreshFolderScopedQueries } from '../../../hooks/purgeFolderScopedQueries';
import { folderSourceAuthorityKeys } from '../folderSourceAuthorityQueryKeys';
import { Logger } from '../../../utils/logger';

/**
 * Generate an upload-batch id for one "Extract N files" click. Format
 * mirrors `bat_<ulid>` so the server-side Zod regex (matching `bat_`
 * prefix + 20–30 Crockford-base32 chars) accepts it. We don't need the
 * full ULID ordering guarantees — `createdAt` already provides
 * time-sort — so this is base32-random rather than time+random.
 *
 * One ULID-equivalent (26 chars) per batch keeps the column compact
 * and the URLs short enough to embed in toast actions.
 *
 * See docs/plans/2026-05-26-process-doc-batch-review-plan.md.
 */
function makeUploadBatchId(): string {
  const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
  const bytes = crypto.getRandomValues(new Uint8Array(13));
  let body = '';
  for (let i = 0; i < bytes.length; i += 1) {
    // 2 base32 chars per byte using the high & low nibbles' 5-bit
    // windows. Yields 26 chars from 13 bytes — matches ULID width.
    body += CROCKFORD[(bytes[i] >> 3) & 0x1f];
    body += CROCKFORD[bytes[i] & 0x1f];
  }
  return `bat_${body}`;
}

export interface FileUploadStageProps {
  manifest: ConnectorManifest;
  folderId: string;
  folderKind: FolderKind;
  target: ImportTarget;
  onBack: () => void;
  onComplete: (result: { createdCount: number; updatedCount: number }) => void;
  onCancel: () => void;
  /**
   * Optional handler invoked when the user picks "Add another" after a
   * successful import. The host should reset back to the picker stage.
   */
  onAddAnother?: () => void;
  /** Optional host-owned next step shown only after a successful import. */
  completionAction?: {
    label: string;
    onClick: () => void;
    pending?: boolean;
    disabled?: boolean;
  };
  /** Optional files captured by an outer drop surface before the modal opens. */
  initialFiles?: readonly File[];
  /** Folder ACL has confirmed that the current user is this folder's owner. */
  canGrantOwnerImportAccess?: boolean;
}

type Phase =
  | 'idle'
  | 'parsing'
  | 'confirming'
  | 'applying'
  | 'success'
  | 'error';

/**
 * Second stage of the {@link ImportSourceModal} for file-format connectors.
 *
 * Pipeline:
 *   1. User drops/pastes/uploads a file.
 *   2. On Apply — lazy-load the adapter via manifest.adapter(),
 *      call adapter.connect({content, filename}) to parse.
 *   3. Fetch records via adapter.fetchRecords(...).
 *   4. POST to /api/folders/:folderId/records (for folder / both targets).
 *      Diagram-only materialization is the next step's job.
 *   5. Surface the created / updated counts to the user.
 *
 * This keeps the component surgical — no adapter-specific UI. Each
 * connector supplies its own inputHint via the manifest.
 */
/**
 * Files the SOP path accepts. Kept in sync with
 * `PROCESS_DOC_UPLOAD_CONTENT_TYPES` in ProcessDocJobController and
 * `resolveSopMimeType` in useCreateSopFileJob — the filter on the
 * client should never accept a file the server would reject.
 *
 * Browsers don't reliably set `file.type` on .docx, so we accept files
 * by extension when the MIME is empty.
 */
const SOP_ACCEPT_EXTENSIONS = ['.pdf', '.docx'] as const;
const SOP_ACCEPT_MIME_TYPES = new Set([
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]);
const GENOGRAM_DOCUMENT_CONNECTOR_ID = 'genogram-case-document';
const GENOGRAM_SOURCE_PROMPT_PREFIX = 'genogram:sourcePrompt:';
const GENOGRAM_SOURCE_ACCEPT_MIME_TYPES = new Set([
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
]);

function isAcceptedSopFile(file: File): boolean {
  const mime = (file.type || '').toLowerCase();
  if (SOP_ACCEPT_MIME_TYPES.has(mime)) return true;
  const name = file.name.toLowerCase();
  return SOP_ACCEPT_EXTENSIONS.some((ext) => name.endsWith(ext));
}

function resolveGenogramSourceContentType(
  fileName: string,
  fileType?: string,
): string {
  const mime = (fileType || '').toLowerCase();
  if (GENOGRAM_SOURCE_ACCEPT_MIME_TYPES.has(mime)) return mime;
  const name = fileName.toLowerCase();
  if (name.endsWith('.pdf')) return 'application/pdf';
  if (name.endsWith('.docx')) {
    return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  }
  if (name.endsWith('.txt')) return 'text/plain';
  return mime;
}

function isAcceptedGenogramSourceFile(file: File): boolean {
  const contentType = resolveGenogramSourceContentType(file.name, file.type);
  return GENOGRAM_SOURCE_ACCEPT_MIME_TYPES.has(contentType);
}

/**
 * Drag-drop with a folder uses the `DataTransferItem` -> entry API,
 * not `dataTransfer.files`. Reading `.files` on a folder drop yields a
 * single entry with no extension and an empty MIME, so the
 * extension/MIME filter (correctly) rejects it as unsupported. To
 * actually batch-upload a dropped folder we need to walk the
 * FileSystemEntry tree the browser hands us via
 * `DataTransferItem.webkitGetAsEntry()` and recursively yank out every
 * file underneath. Same path as the `<input webkitdirectory>` picker
 * — just initiated by a drop event instead of a click.
 *
 * Non-standard but cross-browser. Safari + Firefox + Chromium all
 * implement the same shape, hence the `@ts-expect-error` shims on the
 * call sites where the lib.dom types lag the real API.
 */
async function filesFromDataTransfer(dt: DataTransfer): Promise<File[]> {
  const items = dt.items;
  if (!items || items.length === 0) {
    return Array.from(dt.files ?? []);
  }
  const supportsEntries =
    typeof (items[0] as unknown as { webkitGetAsEntry?: unknown })
      .webkitGetAsEntry === 'function';
  if (!supportsEntries) {
    return Array.from(dt.files ?? []);
  }

  const out: File[] = [];
  const entries: FileSystemEntry[] = [];
  for (let i = 0; i < items.length; i++) {
    const entry = (
      items[i] as unknown as { webkitGetAsEntry: () => FileSystemEntry | null }
    ).webkitGetAsEntry();
    if (entry) entries.push(entry);
  }

  const readFile = (entry: FileSystemFileEntry) =>
    new Promise<File>((resolve, reject) => entry.file(resolve, reject));

  const readDirectory = async (
    dir: FileSystemDirectoryEntry,
  ): Promise<void> => {
    const reader = dir.createReader();
    // readEntries() returns at most ~100 entries per call; keep
    // reading until empty.
    for (;;) {
      const batch: FileSystemEntry[] = await new Promise((resolve, reject) =>
        reader.readEntries(resolve, reject),
      );
      if (batch.length === 0) break;
      for (const e of batch) {
        if (e.isFile) {
          out.push(await readFile(e as FileSystemFileEntry));
        } else if (e.isDirectory) {
          await readDirectory(e as FileSystemDirectoryEntry);
        }
      }
    }
  };

  for (const entry of entries) {
    if (entry.isFile) {
      out.push(await readFile(entry as FileSystemFileEntry));
    } else if (entry.isDirectory) {
      await readDirectory(entry as FileSystemDirectoryEntry);
    }
  }
  return out;
}

type SopUploadStatus = 'queued' | 'uploading' | 'done' | 'failed';
type SopLineageStatus = 'idle' | 'checking' | 'ready' | 'failed';
interface SopUploadEntry {
  key: string;
  file: File;
  status: SopUploadStatus;
  error?: string;
  contentHash?: string;
  lineageStatus?: SopLineageStatus;
  lineageSuggestions?: SourceLineageSuggestion[];
  lineageError?: string;
  selectedSupersedesSourceId?: string | null;
}

function sopUploadEntryKey(file: File): string {
  return `${file.name}:${file.size}:${file.lastModified}`;
}

function isExactContentSuggestion(
  suggestion: SourceLineageSuggestion,
): boolean {
  return suggestion.reasons.includes('content_hash');
}

function sourceLineageDisplayName(suggestion: SourceLineageSuggestion): string {
  return (
    suggestion.source.displayName ??
    suggestion.source.sourceDocumentId ??
    suggestion.source.sourceKey
  );
}

function sourceLineageJobId(
  suggestion: SourceLineageSuggestion,
): string | null {
  return (
    suggestion.completedRun.externalJobId ??
    suggestion.source.latestJobId ??
    null
  );
}

function sopLineageStatusText(entry: SopUploadEntry): string | null {
  if (entry.lineageStatus === 'checking') return ' · checking prior imports';
  if (entry.lineageStatus === 'failed') return ' · lineage check unavailable';
  const suggestion = entry.lineageSuggestions?.[0];
  if (!suggestion) return null;
  return isExactContentSuggestion(suggestion)
    ? ' · identical prior import found'
    : ' · possible previous version found';
}

export const FileUploadStage: React.FC<FileUploadStageProps> = ({
  manifest,
  folderId,
  folderKind,
  target,
  onBack,
  onComplete,
  onCancel,
  onAddAnother,
  completionAction,
  initialFiles,
  canGrantOwnerImportAccess = false,
}) => {
  const defaultGrantOwnerImportAccess =
    canGrantOwnerImportAccess
    && manifest.id === 'csv-upload'
    && folderKind === 'orgchart';
  const [text, setText] = React.useState('');
  const [fileName, setFileName] = React.useState<string | null>(null);
  // SOP path uses a list; single-file connectors continue to use the
  // single-file `fileName` + `text` state above (the file's bytes go
  // straight into `text` for the legacy CSV / Markdown / JSON adapters).
  const [sopFiles, setSopFiles] = React.useState<SopUploadEntry[]>([]);
  const [genogramSourceFile, setGenogramSourceFile] =
    React.useState<File | null>(null);
  const [phase, setPhase] = React.useState<Phase>('idle');
  const [errorMsg, setErrorMsg] = React.useState<string>('');
  const [errorDetail, setErrorDetail] = React.useState<string | null>(null);
  const setImportError = React.useCallback(
    (message: string, detail: string | null = null) => {
      setErrorMsg(message);
      setErrorDetail(detail);
    },
    [],
  );
  // Server failures can carry multi-kilobyte payloads (query dumps,
  // serialized commit JSON). Never render those inline — keep the
  // headline human and park the raw payload behind the details fold.
  const presentApplyError = React.useCallback(
    (err: unknown) => {
      if (err instanceof TabularImportApplyError) {
        setImportError(err.message, err.detail ?? null);
        return;
      }
      const message = err instanceof Error ? err.message : 'apply failed';
      if (message.length > 300 || message.includes('\n')) {
        setImportError('Import failed — the source could not be applied.', message);
        return;
      }
      setImportError(message);
    },
    [setImportError],
  );
  const [result, setResult] = React.useState<{
    createdCount: number;
    updatedCount: number;
    unresolvedCount?: number;
    completionState?: string;
    reconciliationMode?: 'exact' | 'assisted';
    automaticRemovalDetection?: boolean;
  } | null>(null);
  const [warnings, setWarnings] = React.useState<string[]>([]);
  const [grantOwnerImportAccess, setGrantOwnerImportAccess] =
    React.useState(defaultGrantOwnerImportAccess);
  const [ownerAccessProtectedFieldCount, setOwnerAccessProtectedFieldCount] =
    React.useState<number | null>(null);
  const [pendingImport, setPendingImport] =
    React.useState<TabularImportBatch | null>(null);
  const [intentResolution, setIntentResolution] =
    React.useState<SpreadsheetIntentResolution | null>(null);
  const [selectedIntent, setSelectedIntent] = React.useState<string | null>(
    null,
  );
  const [mappingSelections, setMappingSelections] =
    React.useState<SpreadsheetMappingReviewSelection>({});
  /** Rows pre-filled by the column suggester, so the review can label them. */
  const [assistedRoleIds, setAssistedRoleIds] = React.useState<readonly string[]>([]);
  const [reviewStep, setReviewStep] = React.useState<'mapping' | 'reconcile'>('mapping');
  const [mappingAttentionRequestKey, setMappingAttentionRequestKey] =
    React.useState(0);
  const [reviewedMappingProfile, setReviewedMappingProfile] =
    React.useState<SourceMappingProfile | null>(null);
  const [importPreview, setImportPreview] =
    React.useState<TabularImportPreviewResult | null>(null);
  const [isPreviewing, setIsPreviewing] = React.useState(false);
  const [previewError, setPreviewError] = React.useState('');
  const previewErrorRef = React.useRef<HTMLDivElement | null>(null);
  // The error sits below a long mapping table; a click on "Review import"
  // that only paints a message off-screen reads as the button doing nothing.
  React.useEffect(() => {
    if (!previewError) return;
    previewErrorRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [previewError]);

  const isSopDocumentConnector = manifest.id === 'sop-document';
  const isGenogramDocumentConnector =
    manifest.id === GENOGRAM_DOCUMENT_CONNECTOR_ID;
  const availableTemplates = React.useMemo(
    () =>
      (manifest.templates ?? []).filter(
        (template) =>
          !template.folderKinds || template.folderKinds.includes(folderKind),
      ),
    [folderKind, manifest.templates],
  );
  // One batch id per FileUploadStage instance. The stage is re-mounted
  // every time the user opens the modal or clicks "Add another" (which
  // resets `selected` in the parent), so a `useMemo` with an empty dep
  // array is enough — no stale-batch carry-over across opens. The
  // batch id is only consumed when 2+ files are actually dispatched;
  // single-file uploads still pay one trivial UUID's worth of cost.
  const uploadBatchId = React.useMemo(() => makeUploadBatchId(), []);
  const createSopFileJob = useCreateSopFileJob(folderId, uploadBatchId);
  const sourceLineageSuggestions = useSourceLineageSuggestionsMutation();
  const authenticatedFetch = useAuthenticatedFetch();
  const intentResolver = React.useMemo(
    () => new SpreadsheetIntentResolver(),
    [],
  );
  // SOP extraction only: whether to capture compliance info (controls, risks,
  // evidence) from the documents. This drives the folder's Compliance track —
  // when on, enrichment promotes GRC into folder_records AND the Assurance
  // surfaces appear; when off, only process structure is captured (staged
  // disclosure, docs/product/process-user-journey.md §5). Defaults to the
  // folder's current track so the choice reflects existing state.
  const folderProcessConfig = useProcessFolderConfig(
    isSopDocumentConnector ? folderId : undefined,
  );
  const { setProcessFolderConfig } = useSetProcessFolderConfig();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // Switch the folder dashboard to the Reviews tab (optionally deep-linking
  // to an upload batch). Routes through TanStack Router so the param is
  // actually re-read — a raw history.pushState changes the URL without
  // re-rendering. `navigate` is bound to the router, so it stays valid even
  // if the modal unmounts before the user clicks the toast action.
  const goToReviews = React.useCallback(
    (batchId?: string) => {
      void navigate({
        to: '.',
        search: ((prev: Record<string, unknown> | undefined) => {
          const base: Record<string, unknown> = { ...(prev ?? {}) };
          base.view = 'review';
          if (batchId) base.batch = batchId;
          else delete base.batch;
          return base;
        }) as never,
      });
    },
    [navigate],
  );
  // Deep-link to the folder's Sources panel, where unresolved source
  // rows surface for review (process honors the sub-view; other domains
  // render Sources on the folder home).
  const goToSources = React.useCallback(() => {
    void navigate({
      to: '.',
      search: ((prev: Record<string, unknown> | undefined) => ({
        ...(prev ?? {}),
        view: 'sources',
      })) as never,
    });
  }, [navigate]);
  const [captureComplianceOverride, setCaptureComplianceOverride] =
    React.useState<boolean | null>(null);
  const captureCompliance =
    captureComplianceOverride ??
    folderProcessConfig.data?.config.tracksCompliance ??
    false;
  const posthog = usePostHog();

  const folderInputRef = React.useRef<HTMLInputElement | null>(null);
  const consumedInitialFilesRef = React.useRef<readonly File[] | null>(null);

  const clearIntentReview = React.useCallback(() => {
    setPendingImport(null);
    setIntentResolution(null);
    setSelectedIntent(null);
    setMappingSelections({});
    setAssistedRoleIds([]);
    setGrantOwnerImportAccess(defaultGrantOwnerImportAccess);
    setReviewStep('mapping');
    setMappingAttentionRequestKey(0);
    setReviewedMappingProfile(null);
    setImportPreview(null);
    setIsPreviewing(false);
    setPreviewError('');
  }, [defaultGrantOwnerImportAccess]);

  React.useEffect(() => {
    setGrantOwnerImportAccess(defaultGrantOwnerImportAccess);
  }, [defaultGrantOwnerImportAccess]);

  const checkSopLineage = React.useCallback(
    async (entry: SopUploadEntry) => {
      try {
        const contentHash = await sha256FileHash(entry.file);
        const suggestions = await sourceLineageSuggestions.mutateAsync({
          folderId,
          contentHash,
          fileName: entry.file.name,
        });
        setSopFiles((prev) =>
          prev.map((candidate) =>
            candidate.key === entry.key
              ? {
                  ...candidate,
                  contentHash,
                  lineageStatus: 'ready',
                  lineageSuggestions: suggestions,
                  lineageError: undefined,
                }
              : candidate,
          ),
        );
      } catch (error) {
        setSopFiles((prev) =>
          prev.map((candidate) =>
            candidate.key === entry.key
              ? {
                  ...candidate,
                  lineageStatus: 'failed',
                  lineageError:
                    error instanceof Error
                      ? error.message
                      : 'Lineage check failed',
                }
              : candidate,
          ),
        );
      }
    },
    [folderId, sourceLineageSuggestions],
  );

  const addSopFiles = React.useCallback(
    (files: File[]) => {
      const accepted = files.filter(isAcceptedSopFile);
      if (accepted.length === 0) return { added: 0, skipped: files.length };
      const existingKeys = new Set(sopFiles.map((entry) => entry.key));
      const toAdd: SopUploadEntry[] = [];
      for (const file of accepted) {
        const key = sopUploadEntryKey(file);
        if (existingKeys.has(key)) continue;
        existingKeys.add(key);
        toAdd.push({ key, file, status: 'queued', lineageStatus: 'checking' });
      }
      setSopFiles((prev) => {
        // Dedupe by name + size + lastModified (same heuristic browsers
        // use for the File equality dance with drag/drop).
        const seen = new Set(prev.map((e) => e.key));
        return [
          ...prev,
          ...toAdd.filter((entry) => {
            if (seen.has(entry.key)) return false;
            seen.add(entry.key);
            return true;
          }),
        ];
      });
      for (const entry of toAdd) void checkSopLineage(entry);
      setPhase('idle');
      setImportError('');
      setResult(null);
      setWarnings([]);
      clearIntentReview();
      return { added: toAdd.length, skipped: files.length - accepted.length };
    },
    [checkSopLineage, clearIntentReview, setImportError, sopFiles],
  );

  const handleFile = React.useCallback(
    async (file: File | null) => {
      if (!file) return;
      if (isSopDocumentConnector) {
        addSopFiles([file]);
        return;
      }
      if (isGenogramDocumentConnector) {
        if (!isAcceptedGenogramSourceFile(file)) {
          setPhase('error');
          setImportError('Choose a PDF, Word (.docx), or text (.txt) source.');
          return;
        }
        setGenogramSourceFile(file);
        setFileName(file.name);
        setText(
          file.type === 'text/plain' || file.name.toLowerCase().endsWith('.txt')
            ? await file.text()
            : '',
        );
        setPhase('idle');
        setImportError('');
        setResult(null);
        setWarnings([]);
        clearIntentReview();
        return;
      }
      setFileName(file.name);
      const content = await file.text();
      setText(content);
      setPhase('idle');
      setImportError('');
      setResult(null);
      setWarnings([]);
      clearIntentReview();
    },
    [
      isSopDocumentConnector,
      isGenogramDocumentConnector,
      addSopFiles,
      clearIntentReview,
      setImportError,
    ],
  );

  React.useEffect(() => {
    if (!isGenogramDocumentConnector || !initialFiles?.length) return;
    if (consumedInitialFilesRef.current === initialFiles) return;
    consumedInitialFilesRef.current = initialFiles;
    void handleFile(initialFiles[0] ?? null);
  }, [handleFile, initialFiles, isGenogramDocumentConnector]);

  const handleSopMultiSelect = React.useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;
      const summary = addSopFiles(Array.from(files));
      if (summary.skipped > 0 && summary.added === 0) {
        setPhase('error');
        setImportError(
          `Skipped ${summary.skipped} file${summary.skipped === 1 ? '' : 's'} — only PDF and Word (.docx) supported.`,
        );
      } else if (summary.skipped > 0) {
        setWarnings([
          `Skipped ${summary.skipped} non-PDF/Word file${summary.skipped === 1 ? '' : 's'} from the selection.`,
        ]);
      }
    },
    [addSopFiles, setImportError],
  );

  const handleExtractFromFile = React.useCallback(async () => {
    if (sopFiles.length === 0) {
      setPhase('error');
      setImportError('Choose a PDF or Word (.docx) file first.');
      return;
    }
    setPhase('applying');
    setImportError('');

    // Persist the compliance choice on the target folder BEFORE dispatching so
    // the enrichment promotion gate (reads folder tracksCompliance) sees it.
    // Only write when it actually changes. Abort on failure so the user's
    // explicit choice isn't silently dropped.
    const baseConfig =
      folderProcessConfig.data?.config ?? resolveProcessFolderConfig(null);
    if (captureCompliance !== baseConfig.tracksCompliance) {
      try {
        await setProcessFolderConfig(folderId, {
          ...baseConfig,
          tracksCompliance: captureCompliance,
        });
        // Surface the Compliance track immediately across the folder panel.
        queryClient.invalidateQueries({
          queryKey: ['process-folder-config', folderId],
        });
      } catch {
        setCaptureComplianceOverride(baseConfig.tracksCompliance);
        setPhase('error');
        setImportError('Could not save the compliance setting. Try again.');
        return;
      }
    }

    // Sequential AND paced. Server caps upload-URL requests at
    // 60/minute per user (see PROCESS_DOC_UPLOAD_REQUESTS_PER_MINUTE
    // in src/domains/process-doc-jobs/routes.ts). Each file consumes
    // exactly one upload-URL request, so a fixed 1100ms gap between
    // file starts keeps us at ~54/minute — comfortably under the
    // cap with room for poll/refetch traffic and brief network jitter.
    // useCreateSopFileJob also retries once on 429 honoring the
    // server's Retry-After, so a stray race doesn't kill a file.
    const PACING_MS_PER_FILE = 1100;
    let successCount = 0;
    let failureCount = 0;
    const createdJobIds: string[] = [];
    for (let i = 0; i < sopFiles.length; i++) {
      if (i > 0) {
        await new Promise((resolve) => setTimeout(resolve, PACING_MS_PER_FILE));
      }
      setSopFiles((prev) =>
        prev.map((e, idx) => (idx === i ? { ...e, status: 'uploading' } : e)),
      );
      try {
        const createdJob = await createSopFileJob.mutateAsync({
          file: sopFiles[i].file,
          contentHash: sopFiles[i].contentHash,
          supersedesSourceId: sopFiles[i].selectedSupersedesSourceId,
        });
        createdJobIds.push(createdJob.id);
        setSopFiles((prev) =>
          prev.map((e, idx) => (idx === i ? { ...e, status: 'done' } : e)),
        );
        successCount += 1;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'dispatch failed';
        setSopFiles((prev) =>
          prev.map((e, idx) =>
            idx === i ? { ...e, status: 'failed', error: msg } : e,
          ),
        );
        failureCount += 1;
      }
    }

    const out = { createdCount: successCount, updatedCount: 0 };
    setResult(out);
    // Telemetry: fire `batch_review.dispatched` only when >1 file —
    // singletons are the existing per-job path and would noise the
    // metric. `batchSize` is the dispatched-files count, not the
    // selected-files count, so retries don't inflate it.
    if (successCount > 1) {
      posthog?.capture('batch_review.dispatched', {
        folderId,
        uploadBatchId,
        batchSize: successCount,
        failedInBatch: failureCount,
      });
    }
    // Persistent toast so the user has a visible breadcrumb on the
    // dashboard even if they close the modal before reading the inline
    // banner. Dismissible — they're not stuck with it.
    if (successCount > 0) {
      // Batches (2+ successful) deep-link to the per-batch summary in
      // the Reviews tab; singletons drop the batch param and land on
      // the unfiltered Reviews view.
      const isBatch = successCount > 1;
      const reviewAction = {
        label: isBatch ? 'Open batch in Reviews' : 'Open Reviews',
        onClick: () => goToReviews(isBatch ? uploadBatchId : undefined),
      };
      if (failureCount === 0) {
        toast.success(
          successCount === 1
            ? '1 extraction dispatched — landing in Reviews shortly.'
            : `${successCount} extractions dispatched — review them as a batch.`,
          { action: reviewAction },
        );
      } else {
        toast.warning(
          `${successCount} extraction${successCount === 1 ? '' : 's'} dispatched, ${failureCount} failed.`,
          { action: reviewAction },
        );
      }
    } else if (failureCount > 0) {
      toast.error(
        `All ${failureCount} uploads failed — see the modal for per-file errors.`,
      );
    }
    if (successCount === 1 && failureCount === 0 && createdJobIds[0]) {
      window.location.assign(
        `/imports/${encodeURIComponent(createdJobIds[0])}?folderId=${encodeURIComponent(folderId)}`,
      );
      return;
    }
    if (failureCount === 0) {
      setPhase('success');
      onComplete(out);
    } else {
      setPhase(successCount > 0 ? 'success' : 'error');
      if (successCount === 0) {
        setImportError(
          `All ${failureCount} uploads failed. See per-file errors below.`,
        );
      } else {
        setImportError(`${failureCount} of ${sopFiles.length} uploads failed.`);
        onComplete(out);
      }
    }
  }, [
    createSopFileJob,
    onComplete,
    setImportError,
    sopFiles,
    folderId,
    posthog,
    uploadBatchId,
    captureCompliance,
    folderProcessConfig.data,
    setProcessFolderConfig,
    queryClient,
    goToReviews,
  ]);

  const profileId = React.useMemo(
    () => preferredProfileId(manifest, folderKind),
    [folderKind, manifest],
  );
  const importableFieldAliases = React.useMemo(
    () => importableProfileFieldAliases(profileId),
    [profileId],
  );
  const importableFieldEntityRefs = React.useMemo(
    () => importableProfileFieldEntityRefs(profileId),
    [profileId],
  );
  const selectedIntentCandidate = React.useMemo(() => {
    return findSelectedIntentCandidate(intentResolution, selectedIntent);
  }, [intentResolution, selectedIntent]);
  const canConfirmIntent = React.useMemo(
    () =>
      canConfirmIntentResolution(intentResolution, selectedIntent) &&
      canConfirmMappingSelections(selectedIntentCandidate, mappingSelections),
    [
      intentResolution,
      mappingSelections,
      selectedIntent,
      selectedIntentCandidate,
    ],
  );
  const missingRequiredMappings = React.useMemo(
    () =>
      selectedIntentCandidate?.reviewFields.filter(
        (field) => field.required && !mappingSelections[field.id],
      ) ?? [],
    [mappingSelections, selectedIntentCandidate],
  );
  const mappingReviewBlocker = React.useMemo(() => {
    if (!selectedIntent) {
      return 'Choose the closest data type above before reviewing the import.';
    }
    if (missingRequiredMappings.length > 0) {
      return `Map the required ${missingRequiredMappings
        .map((field) => field.label)
        .join(', ')} field${missingRequiredMappings.length === 1 ? '' : 's'} before reviewing the import.`;
    }
    return null;
  }, [missingRequiredMappings, selectedIntent]);
  const handleSelectIntent = React.useCallback(
    (intent: string) => {
      const candidate =
        intentResolution?.candidates.find(
          (intentCandidate) => intentCandidate.intent === intent,
        ) ?? null;
      setSelectedIntent(intent);
      setMappingSelections(
        candidate ? initialMappingSelections(candidate) : {},
      );
      // Suggestions were scoped to the previous intent's role menu.
      setAssistedRoleIds([]);
      setReviewStep('mapping');
      setReviewedMappingProfile(null);
      setImportPreview(null);
      setPreviewError('');
    },
    [intentResolution],
  );
  const sopLineageMatches = React.useMemo(
    () =>
      sopFiles
        .map((entry) => {
          const suggestion = entry.lineageSuggestions?.[0];
          return suggestion ? { entry, suggestion } : null;
        })
        .filter(
          (
            match,
          ): match is {
            entry: SopUploadEntry;
            suggestion: SourceLineageSuggestion;
          } => Boolean(match),
        ),
    [sopFiles],
  );

  const applyParsedImport = React.useCallback(
    async (
      batch: TabularImportBatch,
      mappingProfile?: SourceMappingProfile,
      previewUploadId?: string,
    ) => {
      setPhase('applying');
      setImportError('');

      const out = await applyTabularImport({
        fetcher: authenticatedFetch,
        folderId,
        manifest,
        selectedIntent:
          mappingProfile?.intent ?? manifest.source?.defaultIntent ?? null,
        profileId,
        target,
        batch,
        mappingProfile,
        previewUploadId: previewUploadId ?? null,
        acceptReviewedSchemaChange: Boolean(previewUploadId && mappingProfile),
      });
      if (grantOwnerImportAccess) {
        if (!out.bindingId) {
          setWarnings((current) => [
            ...current,
            'Data was imported, but full Atlas access could not be granted because the saved source binding was unavailable.',
          ]);
        } else {
          try {
            const access = await grantOwnerImportFullAccess({
              fetcher: authenticatedFetch,
              folderId,
              bindingId: out.bindingId,
            });
            setOwnerAccessProtectedFieldCount(access.protectedFieldCount);
            // The durable grant is the completion boundary. Refreshing cached
            // projections expands access from the already-safe pre-grant view,
            // so it must not keep the import dialog waiting on downstream
            // reads. This grant is strictly additive, so a background refresh
            // suffices — the hard purge reset active folder queries to loading,
            // which swapped the page to a skeleton and destroyed this very
            // dialog before the user saw the completion step. Revocation paths
            // still use the shared purge helper.
            void Promise.allSettled([
              queryClient.invalidateQueries({
                queryKey: ['entitlements'],
                refetchType: 'active',
              }),
              refreshFolderScopedQueries(queryClient, folderId),
            ]).then((results) => {
              const failures = results.filter((result) => result.status === 'rejected');
              if (failures.length > 0) {
                Logger.warn('FileUploadStage', 'Post-import cache refresh failed', {
                  folderId,
                  failureCount: failures.length,
                });
              }
            });
          } catch (error) {
            const detail = error instanceof Error ? ` (${error.message})` : '';
            setWarnings((current) => [
              ...current,
              `Data was imported, but full Atlas access could not be granted${detail}. You can retry from Folder settings.`,
            ]);
          }
        }
      }
      if (manifest.id === 'csv-upload' && folderKind === 'orgchart' && out.bindingId) {
        // The import and owner-adoption commands establish the first accepted
        // CSV as the reversible primary for its reviewed intent. Sources is
        // mounted behind this dialog, so mark that one small contract stale
        // before reporting completion; otherwise its 30-second cache can still
        // say "Unconfigured" even though the server already chose the binding.
        // The refetch stays non-blocking just like the protected projections.
        const authorityKey = folderSourceAuthorityKeys.overview(folderId);
        queryClient.removeQueries({
          queryKey: authorityKey,
          exact: true,
          type: 'inactive',
        });
        void queryClient.invalidateQueries({
          queryKey: authorityKey,
          exact: true,
          refetchType: 'active',
        });
      }
      setResult(out);
      clearIntentReview();
      setPhase('success');
      onComplete(out);
    },
    [
      authenticatedFetch,
      clearIntentReview,
      folderId,
      folderKind,
      grantOwnerImportAccess,
      manifest,
      onComplete,
      profileId,
      queryClient,
      setImportError,
      target,
    ],
  );

  const handleReviewImport = React.useCallback(async () => {
    if (!pendingImport || !selectedIntentCandidate) {
      setPreviewError('This upload could not be prepared for review. Choose the file again.');
      return;
    }
    if (!canConfirmIntent) {
      setPreviewError(
        mappingReviewBlocker ??
          'Complete the required mapping choices before reviewing the import.',
      );
      setMappingAttentionRequestKey((current) => current + 1);
      return;
    }
    setIsPreviewing(true);
    setPreviewError('');
    try {
      const mappingProfile = intentResolver.buildReviewedMappingProfile({
        candidate: selectedIntentCandidate,
        headers: headersFromRecords(pendingImport.records),
        selections: mappingSelections,
      });
      const preview = await previewTabularImport({
        fetcher: authenticatedFetch,
        folderId,
        manifest,
        selectedIntent: mappingProfile.intent,
        profileId,
        target,
        batch: pendingImport,
        mappingProfile,
      });
      setReviewedMappingProfile(mappingProfile);
      setImportPreview(preview);
      setReviewStep('reconcile');
    } catch (err) {
      setPreviewError(
        err instanceof Error ? err.message : 'Import preview failed',
      );
    } finally {
      setIsPreviewing(false);
    }
  }, [
    authenticatedFetch,
    canConfirmIntent,
    folderId,
    intentResolver,
    manifest,
    mappingReviewBlocker,
    mappingSelections,
    pendingImport,
    profileId,
    selectedIntentCandidate,
    target,
  ]);

  const handleConfirmImport = React.useCallback(async () => {
    if (
      !pendingImport ||
      !selectedIntentCandidate ||
      !canConfirmIntent ||
      !importPreview ||
      importPreview.reviewBlocking
    ) return;
    try {
      const mappingProfile = reviewedMappingProfile ??
        intentResolver.buildReviewedMappingProfile({
          candidate: selectedIntentCandidate,
          headers: headersFromRecords(pendingImport.records),
          selections: mappingSelections,
        });
      // The records array is the same pendingImport the preview uploaded, so
      // the preview's server-side payload handle can stand in for it. A
      // re-preview replaces importPreview, so the newest id always wins.
      await applyParsedImport(
        pendingImport,
        mappingProfile,
        importPreview.previewUploadId,
      );
    } catch (err) {
      setPhase('error');
      presentApplyError(err);
    }
  }, [
    applyParsedImport,
    canConfirmIntent,
    importPreview,
    intentResolver,
    mappingSelections,
    pendingImport,
    presentApplyError,
    reviewedMappingProfile,
    selectedIntentCandidate,
  ]);

  const handleApply = React.useCallback(async () => {
    if (!text.trim()) {
      setPhase('error');
      setImportError('Paste or choose a file first.');
      return;
    }

    setPhase('parsing');
    setImportError('');

    let adapter;
    try {
      adapter = await manifest.adapter();
    } catch (err) {
      setPhase('error');
      setImportError(
        err instanceof Error ? err.message : 'failed to load adapter',
      );
      return;
    }

    try {
      const connectResult = await adapter.connect({
        content: text,
        filename: fileName ?? undefined,
        accessToken: '',
      } as never);
      if (!connectResult?.success) {
        setPhase('error');
        setImportError(connectResult?.error ?? 'adapter connect failed');
        return;
      }

      const tables = await adapter.listTables();
      const primaryTableId = tables[0]?.id ?? manifest.id;
      const sourceLabel = tables[0]?.name ?? manifest.label;
      const fetched = await fetchAllAdapterRecords(adapter, primaryTableId);
      const records = fetched.records;
      const limitError = tabularImportLimitError({
        loaded: records.length,
        total: fetched.total,
        hasMore: fetched.truncated,
      });
      if (limitError) {
        setWarnings([]);
        setPhase('error');
        setImportError(limitError);
        return;
      }
      setWarnings([]);

      const batch: TabularImportBatch = { sourceLabel, records };
      if (manifest.source?.requiresIntentResolution) {
        const resolution = intentResolver.resolve({
          sourceSystem: manifest.source.system,
          folderKind,
          profileId,
          headers: headersFromRecords(records),
          sampleRows: records.slice(0, 10).map((record) => record.data),
          importableFieldAliases,
          importableFieldEntityRefs,
        });
        const baseSelections = initialMappingSelections(resolution.recommended);
        setPendingImport(batch);
        setIntentResolution(resolution);
        setSelectedIntent(initialSelectedIntentForResolution(resolution));
        setMappingSelections(baseSelections);
        setReviewStep('mapping');
        setReviewedMappingProfile(null);
        setImportPreview(null);
        setPreviewError('');
        setPhase('confirming');

        // Only worth asking when the user is going to review the mapping
        // anyway. Suggestions land as pre-fills behind whatever the user has
        // already chosen, and a failure leaves the deterministic mapping as-is.
        if (resolution.requiresUserConfirmation) {
          const assisted = await fetchAssistedHeaderRoles({
            fetcher: fetch,
            folderId,
            headers: headersFromRecords(records),
            sampleRows: records.slice(0, 10).map((record) => record.data),
            sourceSystem: manifest.source.system,
            profileId,
          });
          if (Object.keys(assisted).length > 0) {
            setMappingSelections((current) => {
              const merged = { ...current };
              for (const [roleId, header] of Object.entries(assisted)) {
                if (merged[roleId]) continue;
                if (Object.values(merged).includes(header)) continue;
                merged[roleId] = header;
              }
              return merged;
            });
            setAssistedRoleIds(Object.keys(assisted));
          }
        }
        return;
      }

      await applyParsedImport(batch);
    } catch (err) {
      setPhase('error');
      presentApplyError(err);
    } finally {
      try {
        await adapter?.disconnect?.();
      } catch {
        // swallow — adapter cleanup is best-effort
      }
    }
  }, [
    applyParsedImport,
    fileName,
    folderId,
    folderKind,
    importableFieldAliases,
    importableFieldEntityRefs,
    intentResolver,
    manifest,
    presentApplyError,
    profileId,
    setImportError,
    text,
  ]);

  const handleGenerateFromGenogramSource = React.useCallback(async () => {
    const hasPastedText = text.trim().length > 0;
    if (!genogramSourceFile && !hasPastedText) {
      setPhase('error');
      setImportError('Choose a PDF, Word, text file, or paste case notes first.');
      return;
    }

    const sourceFileName = genogramSourceFile?.name ?? 'Pasted case notes.txt';
    const contentType = genogramSourceFile
      ? resolveGenogramSourceContentType(
          genogramSourceFile.name,
          genogramSourceFile.type,
        )
      : 'text/plain';
    if (!GENOGRAM_SOURCE_ACCEPT_MIME_TYPES.has(contentType)) {
      setPhase('error');
      setImportError('Choose a PDF, Word (.docx), or text (.txt) source.');
      return;
    }

    setPhase('applying');
    setImportError('');
    setWarnings([]);
    try {
      const body =
        genogramSourceFile ?? new Blob([text.trim()], { type: 'text/plain' });
      const uploadResponse = await authenticatedFetch(
        `/api/folders/${encodeURIComponent(folderId)}/source-documents/upload-url`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fileName: sourceFileName,
            contentType,
            contentLength: body.size,
          }),
        },
      );
      if (!uploadResponse.ok) {
        const errorBody = (await uploadResponse.json().catch(() => ({}))) as {
          error?: string;
        };
        throw new Error(
          errorBody.error ??
            `upload preparation failed (${uploadResponse.status})`,
        );
      }
      const upload = (await uploadResponse.json()) as {
        uploadUrl: string;
        sourceR2Key: string;
        uploadHeaders?: Record<string, string>;
      };

      const putResponse = await authenticatedFetch(upload.uploadUrl, {
        method: 'PUT',
        credentials: 'include',
        headers: upload.uploadHeaders ?? { 'Content-Type': contentType },
        body,
      });
      if (!putResponse.ok) {
        const errorBody = (await putResponse.json().catch(() => ({}))) as {
          error?: string;
        };
        throw new Error(
          errorBody.error ?? `upload failed (${putResponse.status})`,
        );
      }

      const extractResponse = await authenticatedFetch(
        `/api/folders/${encodeURIComponent(folderId)}/source-documents/extract`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sourceR2Key: upload.sourceR2Key,
            fileName: sourceFileName,
            contentType,
          }),
        },
      );
      if (!extractResponse.ok) {
        const errorBody = (await extractResponse.json().catch(() => ({}))) as {
          error?: string;
        };
        throw new Error(
          errorBody.error ??
            `text extraction failed (${extractResponse.status})`,
        );
      }
      const extracted = (await extractResponse.json()) as {
        text?: string;
        textCharCount?: number;
        format?: string;
        pageCount?: number;
      };
      const extractedText = String(extracted.text ?? '').trim();
      if (!extractedText) {
        throw new Error('No readable text was found in this source.');
      }

      const out = await applyTabularImport({
        fetcher: authenticatedFetch,
        folderId,
        manifest,
        selectedIntent:
          manifest.source?.defaultIntent ?? 'genogram_case_source',
        profileId,
        target,
        batch: {
          sourceLabel: sourceFileName,
          records: [
            {
              id: upload.sourceR2Key,
              data: {
                kind: 'genogram_case_source',
                fileName: sourceFileName,
                contentType,
                sourceR2Key: upload.sourceR2Key,
                storageVisibility: 'private',
                textCharCount: extracted.textCharCount ?? extractedText.length,
                format: extracted.format ?? contentType,
                pageCount: extracted.pageCount ?? null,
                generatedAt: new Date().toISOString(),
              },
            },
          ],
        },
      });

      const promptKey = crypto.randomUUID();
      window.sessionStorage.setItem(
        `${GENOGRAM_SOURCE_PROMPT_PREFIX}${promptKey}`,
        extractedText,
      );
      const search = new URLSearchParams({
        type: 'genogram',
        useCase: 'family-therapy',
        startView: 'generate',
        sourcePromptKey: promptKey,
        src: 'folder-source:genogram-case-document',
      });
      if (out.bindingId) search.set('sourceBindingId', out.bindingId);
      window.open(
        `/f/${encodeURIComponent(folderId)}/d/new?${search.toString()}`,
        '_blank',
        'noopener,noreferrer',
      );

      setResult(out);
      clearIntentReview();
      setPhase('success');
      onComplete(out);
      toast.success(
        'Source saved privately. Opening a genogram draft from it.',
      );
    } catch (err) {
      setPhase('error');
      setImportError(
        err instanceof Error
          ? err.message
          : 'failed to save and generate from source',
      );
    }
  }, [
    authenticatedFetch,
    clearIntentReview,
    folderId,
    genogramSourceFile,
    manifest,
    onComplete,
    profileId,
    setImportError,
    target,
    text,
  ]);

  const [isDragging, setIsDragging] = React.useState(false);

  const onDrop = React.useCallback(
    (e: React.DragEvent<HTMLLabelElement>) => {
      e.preventDefault();
      setIsDragging(false);
      if (isSopDocumentConnector) {
        // Walk the FileSystemEntry tree so dropped folders recurse
        // (see filesFromDataTransfer). Then route through the same
        // multi-select path the picker uses for filtering + dedupe.
        void filesFromDataTransfer(e.dataTransfer).then((files) => {
          if (files.length === 0) return;
          const summary = addSopFiles(files);
          if (summary.skipped > 0 && summary.added === 0) {
            setPhase('error');
            setImportError(
              `Skipped ${summary.skipped} file${summary.skipped === 1 ? '' : 's'} — only PDF and Word (.docx) supported.`,
            );
          } else if (summary.skipped > 0) {
            setWarnings([
              `Skipped ${summary.skipped} non-PDF/Word file${summary.skipped === 1 ? '' : 's'} from the dropped folder.`,
            ]);
          }
        });
        return;
      }
      if (isGenogramDocumentConnector) {
        const file = e.dataTransfer.files?.[0] ?? null;
        void handleFile(file);
        return;
      }
      const file = e.dataTransfer.files?.[0] ?? null;
      void handleFile(file);
    },
    [
      addSopFiles,
      handleFile,
      isGenogramDocumentConnector,
      isSopDocumentConnector,
      setImportError,
    ],
  );

  const isBusy = phase === 'parsing' || phase === 'applying' || isPreviewing;
  const importWizardStep: ImportWizardStep =
    phase === 'applying' || phase === 'success'
      ? 'Import'
      : phase === 'confirming' && reviewStep === 'reconcile'
        ? 'Reconcile'
        : phase === 'confirming'
          ? 'Map fields'
          : 'Upload';

  return (
    <div
      className="flex flex-col gap-4"
      data-testid="tid.compass.fileUploadStage"
    >
      <Card
        className="flex-row items-center gap-3 rounded-[14px] border-[color:var(--color-ink-100)] bg-white px-3 py-3 shadow-[0_1px_2px_rgba(13,42,77,0.04)] sm:px-4"
        data-testid="tid.compass.fileUploadStage.connectorSummary"
      >
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="-ml-1 h-8 px-2 text-[color:var(--color-navy-700)] hover:bg-[color:var(--color-navy-100)] hover:text-[color:var(--color-navy-900)]"
        >
          <ArrowLeft className="mr-1 size-3.5" />
          Back
        </Button>
        <span className="flex size-10 flex-shrink-0 items-center justify-center rounded-[10px] border border-[color:var(--color-ink-100)] bg-white p-2 shadow-[0_1px_2px_rgba(13,42,77,0.06)]">
          <ConnectorIcon manifest={manifest} className="size-6" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[14px] font-semibold text-[color:var(--color-navy-900)]">
            {manifest.label}
          </div>
          {manifest.inputHint ? (
            <div className="mt-0.5 truncate text-[12px] text-[color:var(--color-ink-500)]">
              {manifest.inputHint}
            </div>
          ) : null}
        </div>
      </Card>

      {!isSopDocumentConnector && !isGenogramDocumentConnector ? (
        <ImportWizardProgress current={importWizardStep} />
      ) : null}

      {/* Format guidance — tells the user the expected shape before they
          pick a file, and offers a real, parseable sample to start from.
          Driven by the connector manifest (expectedColumns / templates)
          so each file connector documents its own format. */}
      {manifest.expectedColumns?.length || availableTemplates.length ? (
        <Card
          className="gap-3 rounded-[12px] border-[color:var(--color-navy-200)] bg-[color:var(--color-navy-100)] px-4 py-3 shadow-none"
          data-testid="tid.compass.fileUploadStage.formatGuidance"
        >
          {manifest.expectedColumns?.length ? (
            <div className="space-y-1.5">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-[color:var(--color-navy-600)]">
                Expected columns
              </div>
              <div className="flex flex-wrap gap-1.5">
                {manifest.expectedColumns.map((col) => (
                  <span
                    key={col}
                    className="rounded-md bg-white px-2 py-0.5 font-mono text-[11px] text-[color:var(--color-navy-800)] ring-1 ring-inset ring-[color:var(--color-navy-200)]"
                  >
                    {col}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
          {availableTemplates.length ? (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-[color:var(--color-navy-600)]">
                Templates
              </span>
              <div className="flex flex-wrap gap-2">
                {availableTemplates.map((tpl) => (
                  <Button
                    key={tpl.href}
                    asChild
                    variant="outline"
                    size="sm"
                    className="h-8 border-[color:var(--color-navy-200)] bg-white text-[12px] font-semibold text-[color:var(--color-navy-800)] shadow-none hover:border-[color:var(--color-coral-300)] hover:bg-white hover:text-[color:var(--color-navy-900)]"
                  >
                    <a
                      href={tpl.href}
                      download
                      data-testid="tid.compass.fileUploadStage.templateLink"
                    >
                      <Download className="size-3.5" />
                      {tpl.label}
                    </a>
                  </Button>
                ))}
              </div>
            </div>
          ) : null}
        </Card>
      ) : null}

      <div className="space-y-2">
        <Label className="text-[11px] font-semibold uppercase tracking-wide text-[color:var(--color-ink-500)]">
          {isSopDocumentConnector && sopFiles.length > 1 ? 'Files' : 'File'}
        </Label>
        {isSopDocumentConnector && sopFiles.length > 0 ? (
          <div className="space-y-2">
            {/* Fixed 56-px rows + max-h of 5 rows = 280px. Keeps the
                  bottom of the list flush instead of cutting mid-row
                  when there are many files. `overscroll-contain` stops
                  scroll chaining into the dialog scroll once you hit
                  the end of the list. */}
            <ul
              className="max-h-[280px] overflow-y-auto overscroll-contain rounded-md border border-border bg-[color:var(--accent)]/40"
              data-testid="tid.compass.fileUploadStage.fileList"
            >
              {sopFiles.map((entry) => (
                <li
                  key={entry.key}
                  className={cn(
                    'flex h-14 items-center gap-2 border-b border-border/50 px-3 last:border-b-0',
                    entry.status === 'failed' && 'bg-red-50/40',
                    entry.status === 'done' && 'bg-emerald-50/40',
                  )}
                >
                  <span className="flex size-7 flex-shrink-0 items-center justify-center rounded-md bg-[color:var(--primary)]/10 text-primary">
                    {entry.status === 'uploading' ? (
                      <Loader2 className="size-3.5 animate-spin" />
                    ) : entry.status === 'done' ? (
                      <CheckCircle2 className="size-3.5 text-emerald-700" />
                    ) : entry.status === 'failed' ? (
                      <AlertTriangle className="size-3.5 text-red-700" />
                    ) : (
                      <FileText className="size-3.5" />
                    )}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div
                      className="truncate text-sm font-medium text-foreground"
                      title={entry.file.webkitRelativePath || entry.file.name}
                    >
                      {entry.file.webkitRelativePath || entry.file.name}
                    </div>
                    <div className="truncate text-[11px] text-muted-foreground">
                      {entry.file.size < 1024 * 1024
                        ? `${(entry.file.size / 1024).toFixed(1)} KB`
                        : `${(entry.file.size / 1024 / 1024).toFixed(1)} MB`}
                      {entry.status === 'queued' && ' · queued'}
                      {entry.status === 'uploading' && ' · uploading…'}
                      {entry.status === 'done' && ' · done'}
                      {entry.status === 'failed' && entry.error
                        ? ` · failed: ${entry.error}`
                        : entry.status === 'failed'
                          ? ' · failed'
                          : ''}
                      {sopLineageStatusText(entry)}
                    </div>
                  </div>
                  {phase !== 'applying' && entry.status !== 'done' ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7 flex-shrink-0"
                      title="Remove from list"
                      onClick={() => {
                        setSopFiles((prev) =>
                          prev.filter(
                            (candidate) => candidate.key !== entry.key,
                          ),
                        );
                      }}
                    >
                      <X className="size-3.5" />
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
            {phase !== 'applying' ? (
              <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                <span className="whitespace-nowrap">
                  {sopFiles.length} file{sopFiles.length === 1 ? '' : 's'} ready
                </span>
                {/* Right-group action links. flex-wrap + whitespace-nowrap on
                      each label keeps individual labels intact while the group
                      itself can break onto a second line on narrow widths
                      instead of overflowing the modal padding. Separator dots
                      dropped — gap-x-3 is the visual rhythm. */}
                <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1">
                  <label className="cursor-pointer whitespace-nowrap hover:text-foreground">
                    Add more files
                    <input
                      type="file"
                      className="hidden"
                      multiple
                      accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={(e) => {
                        handleSopMultiSelect(e.target.files);
                        e.target.value = '';
                      }}
                    />
                  </label>
                  <button
                    type="button"
                    className="cursor-pointer whitespace-nowrap hover:text-foreground"
                    onClick={() => folderInputRef.current?.click()}
                  >
                    Add folder
                  </button>
                  <button
                    type="button"
                    className="cursor-pointer whitespace-nowrap hover:text-foreground"
                    onClick={() => {
                      setSopFiles([]);
                      setPhase('idle');
                      setImportError('');
                      clearIntentReview();
                    }}
                  >
                    Clear all
                  </button>
                </div>
              </div>
            ) : null}
            {sopLineageMatches.length > 0 ? (
              <div
                className="rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-950"
                data-testid="tid.compass.fileUploadStage.sourceLineage"
              >
                <div className="flex items-center gap-1.5 font-semibold">
                  <Link2 className="size-3.5" />
                  Prior source matches
                </div>
                <ul className="mt-2 space-y-2">
                  {sopLineageMatches.map(({ entry, suggestion }) => {
                    const exactMatch = isExactContentSuggestion(suggestion);
                    const priorJobId = sourceLineageJobId(suggestion);
                    const priorName = sourceLineageDisplayName(suggestion);
                    return (
                      <li
                        key={`${entry.key}:${suggestion.source.id}`}
                        className="flex flex-wrap items-center justify-between gap-2 rounded border border-blue-100 bg-white/80 px-2 py-1.5"
                      >
                        <div className="min-w-0">
                          <div className="truncate font-medium text-blue-950">
                            {entry.file.name}
                          </div>
                          <div className="mt-0.5 text-[11px] text-blue-800">
                            {exactMatch
                              ? `Identical to ${priorName}; the previous read can be reused.`
                              : `Looks like a newer version of ${priorName}.`}
                          </div>
                        </div>
                        <div className="flex flex-shrink-0 items-center gap-2">
                          {!exactMatch ? (
                            <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-blue-100 bg-blue-50 px-2 py-1 text-[11px] font-medium text-blue-900 hover:bg-blue-100">
                              <input
                                type="checkbox"
                                className="size-3"
                                checked={
                                  entry.selectedSupersedesSourceId ===
                                  suggestion.source.id
                                }
                                onChange={(event) => {
                                  const selected = event.target.checked
                                    ? suggestion.source.id
                                    : null;
                                  setSopFiles((prev) =>
                                    prev.map((candidate) =>
                                      candidate.key === entry.key
                                        ? {
                                            ...candidate,
                                            selectedSupersedesSourceId:
                                              selected,
                                          }
                                        : candidate,
                                    ),
                                  );
                                }}
                              />
                              Link as previous version
                            </label>
                          ) : null}
                          {priorJobId ? (
                            <a
                              href={`/imports/${encodeURIComponent(priorJobId)}?folderId=${encodeURIComponent(folderId)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center rounded-md border border-blue-100 bg-white px-2 py-1 text-[11px] font-medium text-blue-900 hover:bg-blue-50"
                            >
                              Open prior
                            </a>
                          ) : null}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
          </div>
        ) : fileName && !isSopDocumentConnector ? (
          <Card className="flex-row items-center gap-3 rounded-[12px] border-[color:var(--color-ink-100)] bg-white px-3 py-3 shadow-[0_1px_2px_rgba(13,42,77,0.04)]">
            <span className="flex size-9 flex-shrink-0 items-center justify-center rounded-[9px] border border-[color:var(--color-navy-200)] bg-[color:var(--color-navy-100)] text-[color:var(--color-navy-800)]">
              <FileText className="size-4" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[13px] font-semibold text-[color:var(--color-navy-900)]">
                {fileName}
              </div>
              <div className="mt-0.5 text-[11.5px] text-[color:var(--color-ink-500)]">
                {isGenogramDocumentConnector && genogramSourceFile
                  ? `${(genogramSourceFile.size / 1024).toFixed(1)} KB · ready to generate`
                  : `${(text.length / 1024).toFixed(1)} KB read · ready to import`}
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="size-7"
              title="Remove file"
              onClick={() => {
                setFileName(null);
                setText('');
                setGenogramSourceFile(null);
                setPhase('idle');
                setImportError('');
                clearIntentReview();
              }}
            >
              <X className="size-3.5" />
            </Button>
          </Card>
        ) : (
          <label
            onDragOver={(e) => {
              e.preventDefault();
              if (!isDragging) setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={onDrop}
            data-testid="tid.compass.fileUploadStage.dropzone"
            className={cn(
              'flex cursor-pointer flex-col items-center justify-center gap-3 rounded-[14px] border border-dashed border-[color:var(--color-ink-200)] bg-white px-6 py-10 text-center shadow-[0_1px_2px_rgba(13,42,77,0.04)] transition-[border-color,background-color,box-shadow,transform]',
              'hover:-translate-y-0.5 hover:border-[color:var(--color-coral-300)] hover:bg-[color:var(--color-coral-100)]/35 hover:shadow-[0_8px_24px_rgba(13,42,77,0.08)]',
              isDragging &&
                'border-[color:var(--color-coral-400)] bg-[color:var(--color-coral-100)] shadow-[0_8px_24px_rgba(13,42,77,0.08)]',
            )}
          >
            <span className="flex size-11 items-center justify-center rounded-[10px] border border-[color:var(--color-coral-200)] bg-[color:var(--color-coral-100)] text-[color:var(--color-coral-600)]">
              <Upload className="size-[18px]" />
            </span>
            <div>
              <div className="text-[14px] font-semibold text-[color:var(--color-navy-900)]">
                {isSopDocumentConnector
                  ? 'Drop files or a folder here, or click to choose'
                  : isGenogramDocumentConnector
                    ? 'Drop a case document here or click to choose'
                    : 'Drop a file here or click to choose'}
              </div>
              <div className="mx-auto mt-1 max-w-2xl text-[12px] leading-5 text-[color:var(--color-ink-500)]">
                {isSopDocumentConnector
                  ? 'PDF or Word (.docx) — drop multiple files or a whole folder. Each becomes its own extraction job.'
                  : isGenogramDocumentConnector
                    ? 'PDF, Word (.docx), text (.txt), or paste notes below. Originals are stored privately.'
                    : (manifest.inputHint ??
                      'Any file matching this connector')}
              </div>
            </div>
            <input
              type="file"
              className="hidden"
              multiple={isSopDocumentConnector}
              accept={
                isSopDocumentConnector
                  ? '.pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                  : isGenogramDocumentConnector
                    ? '.pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain'
                    : undefined
              }
              onChange={(e) => {
                if (isSopDocumentConnector) {
                  handleSopMultiSelect(e.target.files);
                } else {
                  void handleFile(e.target.files?.[0] ?? null);
                }
                e.target.value = '';
              }}
            />
            {isSopDocumentConnector ? (
              <button
                type="button"
                className="text-[11.5px] font-semibold text-[color:var(--color-navy-700)] underline-offset-4 hover:text-[color:var(--color-navy-900)] hover:underline"
                onClick={(e) => {
                  e.preventDefault();
                  folderInputRef.current?.click();
                }}
              >
                Or pick a whole folder
              </button>
            ) : null}
          </label>
        )}
        {/* Hidden folder picker — `webkitdirectory` makes the browser
              file dialog behave as a folder picker and recursively
              hand us every file inside. We filter to PDF/DOCX in
              handleSopMultiSelect. Lives outside the drop label so the
              "Add folder" link on the populated state can trigger it. */}
        {isSopDocumentConnector ? (
          <input
            ref={folderInputRef}
            type="file"
            className="hidden"
            multiple
            // @ts-expect-error — `webkitdirectory` is a non-standard
            // but cross-browser-supported attribute for folder pick.
            webkitdirectory="true"
            directory="true"
            onChange={(e) => {
              handleSopMultiSelect(e.target.files);
              e.target.value = '';
            }}
          />
        ) : null}
        {!isSopDocumentConnector && (
          <details className="group overflow-hidden rounded-[10px] border border-[color:var(--color-ink-100)] bg-white text-[12px] text-[color:var(--color-ink-500)]">
            <summary className="cursor-pointer select-none px-3.5 py-2.5 font-semibold text-[color:var(--color-navy-700)] transition-colors hover:bg-[color:var(--color-ink-50)] hover:text-[color:var(--color-navy-900)]">
              Or paste contents directly
            </summary>
            <div className="border-t border-[color:var(--color-ink-100)] p-3">
              <Textarea
                value={text}
                onChange={(e) => {
                  setText(e.target.value);
                  if (isGenogramDocumentConnector) {
                    setGenogramSourceFile(null);
                    setFileName(
                      e.target.value.trim() ? 'Pasted case notes.txt' : null,
                    );
                  }
                  setPhase('idle');
                  setImportError('');
                  clearIntentReview();
                }}
                placeholder="Paste file contents…"
                spellCheck={false}
                wrap="soft"
                className="h-32 w-full resize-none overflow-auto break-all whitespace-pre-wrap rounded-[8px] border-[color:var(--color-ink-200)] bg-white font-mono text-[11px] focus-visible:border-[color:var(--color-coral-300)] focus-visible:ring-[color:var(--color-coral-100)]"
                data-testid="tid.compass.fileUploadStage.textarea"
              />
            </div>
          </details>
        )}
      </div>

      {phase === 'confirming' &&
      reviewStep === 'mapping' &&
      intentResolution &&
      selectedIntentCandidate ? (
        <SourceIntentReviewPanel
          resolution={intentResolution}
          selectedCandidate={selectedIntentCandidate}
          selectedIntent={selectedIntent}
          headers={headersFromRecords(pendingImport?.records ?? [])}
          sampleRows={(pendingImport?.records ?? [])
            .slice(0, 10)
            .map((record) => record.data)}
          mappingSelections={mappingSelections}
          assistedRoleIds={assistedRoleIds}
          rowCount={pendingImport?.records.length ?? 0}
          columnCount={headersFromRecords(pendingImport?.records ?? []).length}
          attentionRequestKey={mappingAttentionRequestKey}
          onSelectIntent={handleSelectIntent}
          onMappingChange={(fieldId, sourceField) => {
            setMappingSelections((current) => ({
              ...current,
              [fieldId]: sourceField,
            }));
            // Once the user touches a row it is theirs, not a suggestion.
            setAssistedRoleIds((current) => current.filter((id) => id !== fieldId));
            setReviewedMappingProfile(null);
            setImportPreview(null);
            setPreviewError('');
          }}
        />
      ) : null}

      {phase === 'confirming' && reviewStep === 'reconcile' && importPreview ? (
        <SourceImportReconciliationPanel
          preview={importPreview}
          ownerImportAccess={
            defaultGrantOwnerImportAccess
              ? {
                  selected: grantOwnerImportAccess,
                  onChange: setGrantOwnerImportAccess,
                }
              : undefined
          }
        />
      ) : null}

      {previewError ? (
        <div
          ref={previewErrorRef}
          role="alert"
          className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-xs text-red-900"
          data-testid="tid.compass.fileUploadStage.previewError"
        >
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-red-600" />
          {previewError}
        </div>
      ) : null}

      {phase === 'error' ? (
        <div
          className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2.5"
          data-testid="tid.compass.fileUploadStage.error"
        >
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-red-600" />
          <div className="min-w-0 flex-1 text-xs leading-snug text-red-900">
            {errorMsg}
            {errorDetail ? (
              <details className="mt-1.5">
                <summary
                  className="cursor-pointer select-none text-[11px] font-medium text-red-800 hover:underline"
                  data-testid="tid.compass.fileUploadStage.errorDetails"
                >
                  Technical details
                </summary>
                <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-all rounded bg-red-100/60 p-2 font-mono text-[10px] leading-relaxed text-red-900">
                  {errorDetail}
                </pre>
              </details>
            ) : null}
          </div>
        </div>
      ) : null}

      {warnings.length > 0 && (phase === 'confirming' || phase === 'success') ? (
        <div
          className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5"
          data-testid="tid.compass.fileUploadStage.warnings"
        >
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600" />
          <ul className="space-y-1 text-xs leading-snug text-amber-900">
            {warnings.map((warning, index) => (
              <li key={`${index}-${warning}`}>{warning}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {phase === 'success' && result ? (
        <div
          className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2.5"
          data-testid="tid.compass.fileUploadStage.success"
        >
          <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-700" />
          <div className="flex-1 text-xs leading-snug text-emerald-900">
            {isSopDocumentConnector
              ? result.createdCount === 1
                ? 'Capture job dispatched. Each finished extraction will land in the Reviews tab for you to approve.'
                : `${result.createdCount} capture jobs dispatched. Each finished extraction will land in the Reviews tab for you to approve.`
              : isGenogramDocumentConnector
                ? 'Source saved privately and opened in a new genogram draft.'
                : folderKind === 'orgchart'
                  ? `Imported ${result.createdCount} new record${result.createdCount === 1 ? '' : 's'}${result.updatedCount > 0 ? `, updated ${result.updatedCount}` : ''}. People, positions, reporting lines, and staffing are now available in Atlas.`
                  : `Imported ${result.createdCount} new record${result.createdCount === 1 ? '' : 's'}${result.updatedCount > 0 ? `, updated ${result.updatedCount}` : ''}.`}
            {ownerAccessProtectedFieldCount !== null ? (
              <div
                className="mt-1"
                data-testid="tid.compass.fileUploadStage.ownerAccessGranted"
              >
                You now have full Atlas access for this folder, including succession planning, workload, scenarios, protected data, exports, assistant features, and source management.
              </div>
            ) : null}
            {!isSopDocumentConnector &&
            !isGenogramDocumentConnector &&
            folderKind === 'orgchart' &&
            result.reconciliationMode === 'assisted' ? (
              <div
                className="mt-1 text-amber-800"
                data-testid="tid.compass.fileUploadStage.assistedReconciliationSummary"
              >
                Atlas used the identities available in this file. Future
                updates will keep unmatched existing records for confirmation
                instead of removing them automatically.
              </div>
            ) : null}
            {!isSopDocumentConnector &&
            !isGenogramDocumentConnector &&
            (result.unresolvedCount ?? 0) > 0 ? (
              <div
                className="mt-1 text-amber-800"
                data-testid="tid.compass.fileUploadStage.unresolvedSummary"
              >
                {result.unresolvedCount} row
                {result.unresolvedCount === 1 ? '' : 's'} could not be matched
                automatically — resolve them from this folder&apos;s Sources
                panel.
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {isSopDocumentConnector && phase !== 'success' ? (
        <label
          className="flex cursor-pointer items-start gap-2.5 rounded-md border border-border bg-[color:var(--accent)]/30 px-3 py-2.5"
          data-testid="tid.compass.fileUploadStage.captureCompliance"
        >
          <Checkbox
            checked={captureCompliance}
            onCheckedChange={(v) => setCaptureComplianceOverride(v === true)}
            disabled={isBusy}
            className="mt-0.5"
          />
          <span className="text-xs leading-snug text-foreground">
            <span className="font-medium">Capture compliance info</span>
            <span className="text-muted-foreground">
              {' '}
              — extract controls, risks, and evidence per step, and turn on this
              folder&apos;s Compliance track (Assurance + fidelity). Leave off
              to capture process structure only.
            </span>
          </span>
        </label>
      ) : null}

      <div
        className="sticky bottom-0 z-20 mt-1 flex flex-wrap items-center justify-end gap-2 rounded-[12px] border border-[color:var(--color-ink-100)] bg-white/95 px-3 py-3 shadow-[0_-4px_16px_rgba(13,42,77,0.05)] backdrop-blur sm:px-4"
        data-testid="tid.compass.fileUploadStage.actions"
      >
        {phase === 'confirming' && reviewStep === 'mapping' && mappingReviewBlocker ? (
          <div
            className="mr-auto min-w-0 flex-1 basis-full text-[11.5px] leading-snug text-amber-800 sm:basis-64"
            data-testid="tid.compass.fileUploadStage.mappingBlocker"
            aria-live="polite"
          >
            <AlertTriangle className="mr-1 inline size-3.5 align-[-2px]" />
            {mappingReviewBlocker}
          </div>
        ) : null}
        <Button
          variant="outline"
          size="sm"
          onClick={onCancel}
          className="border-[color:var(--color-ink-200)] text-[color:var(--color-navy-800)] shadow-none hover:border-[color:var(--color-coral-300)] hover:bg-white hover:text-[color:var(--color-navy-900)]"
        >
          {phase === 'success' ? 'Close' : 'Cancel'}
        </Button>
        {phase === 'confirming' && reviewStep === 'reconcile' ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setReviewStep('mapping');
              setPreviewError('');
            }}
            disabled={isBusy}
            className="border-[color:var(--color-ink-200)] text-[color:var(--color-navy-800)] shadow-none hover:border-[color:var(--color-coral-300)] hover:bg-white hover:text-[color:var(--color-navy-900)]"
            data-testid="tid.compass.fileUploadStage.backToMapping"
          >
            Back to mapping
          </Button>
        ) : null}
        {phase === 'success' && onAddAnother ? (
          <Button
            variant="outline"
            size="sm"
            onClick={onAddAnother}
            className="border-[color:var(--color-ink-200)] text-[color:var(--color-navy-800)] shadow-none hover:border-[color:var(--color-coral-300)] hover:bg-white hover:text-[color:var(--color-navy-900)]"
          >
            Add more
          </Button>
        ) : null}
        {phase === 'success' &&
        isSopDocumentConnector &&
        result &&
        result.createdCount > 0 ? (
          <Button
            size="sm"
            variant="navy"
            onClick={() => {
              const isBatch = result.createdCount > 1;
              goToReviews(isBatch ? uploadBatchId : undefined);
              onCancel();
              toast.success(
                result.createdCount === 1
                  ? 'Extraction running. Refresh the Reviews tab to see it land.'
                  : `${result.createdCount} extractions running — review them as a batch.`,
              );
            }}
            data-testid="tid.compass.fileUploadStage.openReviews"
          >
            {result.createdCount > 1 ? 'Open batch in Reviews' : 'Open Reviews'}
            <ArrowRight className="ml-1 size-3.5" />
          </Button>
        ) : null}
        {phase === 'success' &&
        !isSopDocumentConnector &&
        !isGenogramDocumentConnector &&
        result &&
        (result.unresolvedCount ?? 0) > 0 ? (
          <Button
            size="sm"
            variant="navy"
            onClick={() => {
              goToSources();
              onCancel();
            }}
            data-testid="tid.compass.fileUploadStage.openSources"
          >
            Review unresolved rows
            <ArrowRight className="ml-1 size-3.5" />
          </Button>
        ) : null}
        {phase === 'success' &&
        result &&
        result.createdCount + result.updatedCount > 0 &&
        completionAction ? (
          <Button
            size="sm"
            variant="navy"
            onClick={completionAction.onClick}
            disabled={completionAction.disabled || completionAction.pending}
            data-testid="tid.compass.fileUploadStage.completionAction"
          >
            {completionAction.pending ? (
              <Loader2 className="mr-1 size-3.5 animate-spin" />
            ) : null}
            {completionAction.label}
            {!completionAction.pending ? (
              <ArrowRight className="ml-1 size-3.5" />
            ) : null}
          </Button>
        ) : null}
        {phase !== 'success' ? (
          <Button
            size="sm"
            variant="navy"
            onClick={
              isSopDocumentConnector
                ? handleExtractFromFile
                : isGenogramDocumentConnector
                  ? handleGenerateFromGenogramSource
                  : phase === 'confirming'
                    ? reviewStep === 'reconcile'
                      ? handleConfirmImport
                      : handleReviewImport
                    : handleApply
            }
            disabled={
              isSopDocumentConnector
                ? isBusy || sopFiles.length === 0
                : isGenogramDocumentConnector
                  ? isBusy || (!genogramSourceFile && !text.trim())
                  : isBusy ||
                    (phase === 'confirming'
                      ? reviewStep === 'reconcile'
                        ? !importPreview || importPreview.reviewBlocking
                        : false
                      : !text.trim())
            }
            data-testid="tid.compass.fileUploadStage.applyButton"
          >
            {isBusy ? <Loader2 className="mr-1 size-3.5 animate-spin" /> : null}
            {isSopDocumentConnector
              ? phase === 'applying'
                ? sopFiles.length > 1
                  ? `Uploading ${sopFiles.filter((e) => e.status === 'done').length + 1} of ${sopFiles.length}…`
                  : 'Starting…'
                : sopFiles.length > 1
                  ? `Extract ${sopFiles.length} files`
                  : 'Extract'
              : isGenogramDocumentConnector
                ? phase === 'applying'
                  ? 'Saving...'
                  : 'Save and generate'
                : phase === 'parsing'
                  ? 'Parsing…'
                  : isPreviewing
                    ? 'Checking changes…'
                  : phase === 'applying'
                    ? 'Adding…'
                    : phase === 'confirming'
                      ? reviewStep === 'reconcile'
                        ? 'Import records'
                        : 'Review import'
                      : 'Add to folder'}
          </Button>
        ) : null}
      </div>
    </div>
  );
};
